import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Store,
  ShoppingBasket,
  Utensils,
  ShoppingBag,
  MessageSquare,
  CreditCard,
  Settings,
  BarChart3,
  Shield,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  User,
  Megaphone,
  Globe
} from 'lucide-react';
import namkeLogoHeader from '../../assets/namke-logo-header.png';
import {
  getAllUsers,
  getAllRestaurants,
  getAllDishes,
  getAdminNotifications,
  getAdminNotificationsUnreadCount,
  markAdminNotificationsSeen,
} from '../../services/adminApi';

const NOTIFICATION_TYPE_ICONS = {
  order: '🛒',
  restaurant: '🏪',
  payment: '💳',
};

function formatNotificationTimestamp(isoString) {
  const date = new Date(isoString);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

const SEARCH_RESULT_LIMIT = 5;

const AdminLayout = ({ children }) => {
  const TERRACOTTA = 'rgb(239, 76, 35)';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({ users: [], restaurants: [], dishes: [] });
  const location = useLocation();
  const navigate = useNavigate();

  // Recherche globale : interroge users/restaurants/dishes et affiche les correspondances
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) {
      setSearchResults({ users: [], restaurants: [], dishes: [] });
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const [users, restaurants, dishes] = await Promise.all([
          getAllUsers().catch(() => []),
          getAllRestaurants().catch(() => []),
          getAllDishes().catch(() => []),
        ]);
        const matches = (list, getText) =>
          (Array.isArray(list) ? list : []).filter((item) =>
            getText(item)?.toLowerCase().includes(term)
          ).slice(0, SEARCH_RESULT_LIMIT);

        setSearchResults({
          users: matches(users, (u) => u.email),
          restaurants: matches(restaurants, (r) => r.name),
          dishes: matches(dishes, (d) => d.name),
        });
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const goToSearchResult = (path) => {
    navigate(path);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const hasSearchResults =
    searchResults.users.length > 0 ||
    searchResults.restaurants.length > 0 ||
    searchResults.dishes.length > 0;

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/admin/users', icon: Users, label: 'Utilisateurs' },
    { path: '/admin/restaurants', icon: Store, label: 'Restaurants' },
    { path: '/admin/grocery-shops', icon: ShoppingBasket, label: 'Grocery shop' },
    { path: '/admin/reference-data', icon: Globe, label: 'Pays & référence' },
    { path: '/admin/dishes', icon: Utensils, label: 'Plats' },
    { path: '/admin/orders', icon: ShoppingBag, label: 'Commandes' },
    { path: '/admin/complaints', icon: MessageSquare, label: 'Réclamations' },
    { path: '/admin/payments', icon: CreditCard, label: 'Paiements' },
    { path: '/admin/announcements', icon: Megaphone, label: 'Annonces' },
    { path: '/admin/promotion-pricing', icon: BarChart3, label: 'Tarification Promotions' },
    { path: '/admin/admins', icon: Shield, label: 'Administrateurs' },
    { path: '/admin/promotion-insights', icon: BarChart3, label: 'Insights Promotions' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' }
  ];

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    navigate('/admin-login');
  };

  // Fermer les dropdowns quand on clique à l'extérieur
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
      if (showProfile && !event.target.closest('.profile-dropdown')) {
        setShowProfile(false);
      }
      if (showSearchResults && !event.target.closest('.search-dropdown')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showProfile, showSearchResults]);

  // Badge (nombre de nouveautés) chargé au montage
  useEffect(() => {
    let cancelled = false;
    getAdminNotificationsUnreadCount()
      .then((data) => {
        if (!cancelled) setUnreadCount(data?.unread_count || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Ouverture du panneau : charge la liste et marque les notifications comme vues
  const handleToggleNotifications = () => {
    const opening = !showNotifications;
    setShowNotifications(opening);
    if (!opening) return;

    setNotificationsLoading(true);
    getAdminNotifications()
      .then((data) => setNotifications(Array.isArray(data) ? data : []))
      .catch(() => setNotifications([]))
      .finally(() => setNotificationsLoading(false));

    markAdminNotificationsSeen()
      .then(() => setUnreadCount(0))
      .catch(() => {});
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="dashboard-shell min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col`}>
        
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <img src={namkeLogoHeader} alt="namke" className="h-10 w-auto object-contain" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-2 py-2 text-sm font-semibold rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'text-white border-r-2'
                        : 'text-gray-800 hover:bg-orange-50'
                    }`}
                    style={isActive(item.path) ? { 
                      backgroundColor: 'rgb(5, 70, 37)', 
                      borderColor: TERRACOTTA
                    } : {}}
                  >
                    <Icon size={20} className="mr-3 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout button */}
        <div className="px-2 py-3 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-2 py-3 text-sm font-semibold text-gray-800 hover:bg-orange-50 rounded-lg transition-colors"
          >
            <LogOut size={20} className="mr-3" />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:ml-64 min-h-screen min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <Menu size={20} />
            </button>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-lg mx-4 hidden md:block">
              <div className="relative search-dropdown">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur, un restaurant, un plat..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSearchResults(true);
                  }}
                  onFocus={() => setShowSearchResults(true)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)] text-sm"
                />

                {showSearchResults && searchTerm.trim().length >= 2 && (
                  <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    {searchLoading && (
                      <div className="p-4 text-sm text-gray-500">Recherche...</div>
                    )}
                    {!searchLoading && !hasSearchResults && (
                      <div className="p-4 text-sm text-gray-500">Aucun résultat pour « {searchTerm} »</div>
                    )}
                    {!searchLoading && searchResults.users.length > 0 && (
                      <div className="border-b border-gray-100">
                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase">Utilisateurs</div>
                        {searchResults.users.map((u) => (
                          <button
                            key={`user-${u.id}`}
                            onClick={() => goToSearchResult('/admin/users')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                          >
                            {u.email}
                          </button>
                        ))}
                      </div>
                    )}
                    {!searchLoading && searchResults.restaurants.length > 0 && (
                      <div className="border-b border-gray-100">
                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase">Restaurants</div>
                        {searchResults.restaurants.map((r) => (
                          <button
                            key={`restaurant-${r.id}`}
                            onClick={() => goToSearchResult('/admin/restaurants')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                          >
                            {r.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {!searchLoading && searchResults.dishes.length > 0 && (
                      <div>
                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase">Plats</div>
                        {searchResults.dishes.map((d) => (
                          <button
                            key={`dish-${d.id}`}
                            onClick={() => goToSearchResult('/admin/dishes')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                          >
                            {d.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side actions */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative notification-dropdown">
                <button
                  onClick={handleToggleNotifications}
                  className="p-2 text-[rgb(5,70,37)] hover:text-[rgb(239,76,35)] hover:bg-orange-50 rounded-lg relative"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[rgb(239,76,35)] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notificationsLoading ? (
                        <div className="p-4 text-center text-sm text-gray-400">Chargement...</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-400">Aucune notification récente.</div>
                      ) : (
                        notifications.map((n, i) => (
                          <div
                            key={`${n.type}-${n.created_at}-${i}`}
                            className={`p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${n.is_new ? 'bg-orange-50/40' : ''}`}
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {NOTIFICATION_TYPE_ICONS[n.type] || ''} {n.title}
                            </p>
                            <p className="text-xs text-gray-500">{n.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{formatNotificationTimestamp(n.created_at)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Profile */}
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900"
                >
                  <div className="w-8 h-8 bg-orange-100 text-[rgb(239,76,35)] rounded-full flex items-center justify-center">
                    <User size={16} />
                  </div>
                  <span className="hidden md:block text-sm font-medium">Admin</span>
                </button>
                
                {/* Profile dropdown */}
                {showProfile && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">Administrateur</p>
                      <p className="text-xs text-gray-500">admin@namke.app</p>
                    </div>
                    <div className="py-2">
                      <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50">
                        Mon profil
                      </button>
                      <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50">
                        Paramètres
                      </button>
                      <button 
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children || <Outlet />}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;


